import { cn } from '@/lib/cn';
import { isDataSourceScopedDataStream } from '@squaredup/data-streams';
import { AgentGroupId, Serialised } from '@squaredup/ids';
import { hasHandlebarsPlaceholders, UIConfig } from '@squaredup/utilities';
import Button from 'components/button/Button';
import { getOptionData, getOptionValue } from 'components/forms/autocomplete/Autocomplete';
import DisplayJsonUi, { getAllFieldsByName } from 'components/forms/jsonForms/DisplayJsonUi';
import { useDOMElement } from 'components/hooks/useDOMElement';
import Tooltip from 'components/tooltip/Tooltip';
import PluginContext from 'contexts/PluginContext';
import { useDataStreamConfigToRequest } from 'dashboard-engine/hooks/useDataStreamConfigToRequest';
import { type Config, type Plugin, type ProjectedDataStreamDefinitionEntity } from 'dynamo-wrapper';
import stringify from 'fast-json-stable-stringify';
import { useFlag } from 'lib/useFlag';
import { cloneDeep, debounce, noop } from 'lodash';
import { TestConfigAPI } from 'pages/settings/plugins/PluginTest';
import { PluginConfigFormData } from 'pages/settings/plugins/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import type { DataStream } from 'services/DataStreamDefinitionService';
import { resolveDataSourceConfig } from 'services/DataStreamService';
import { useDatasetContext } from 'ui/editor/dataStream/contexts/DatasetContext';
import { useFormChange } from 'ui/editor/dataStream/hooks/useFormChange';
import { useTileEditorStepsContext } from '../../contexts/TileEditorStepsContext';
import { useDataStreamForm } from '../../hooks/useConfigurableDataStreamForm';
import { useTileEditorStore } from '../../state/TileEditorStoreProvider';
import { toTileEditorDataStream } from '../../state/useTileEditorDataStreams';
import { StepProgressButton } from '../StepProgressButton';
import { SaveAsDataStreamModal } from './SaveAsDataStream/SaveAsDataStreamModal';
import { isSavedDataStream } from './SavedDataStreams';

export interface DataStreamConfigureFormProps {
    pluginConfig: Serialised<Config<object>> | undefined;
    plugin: Serialised<Plugin> | undefined;
    formTemplate: Serialised<UIConfig[]> | undefined;
    formDefaultValues: Record<string, any> | undefined;
    streamDefinitions: Serialised<ProjectedDataStreamDefinitionEntity>[];
    dataStream: DataStream;
    manualConfigApply?: boolean;
    className?: string;
    trackFormChanges: (values: Record<string, any>) => void;
}

export const DataStreamConfigureForm = ({
    formTemplate,
    formDefaultValues,
    streamDefinitions,
    dataStream,
    plugin,
    pluginConfig,
    manualConfigApply,
    className,
    trackFormChanges
}: DataStreamConfigureFormProps) => {
    const { config, setConfig } = useDatasetContext();
    const { setCurrentEditorStep } = useTileEditorStepsContext();
    const allowDataStreamSaving = useFlag('dataStreamSaving');

    /**
     * Ensures that the modal is reset when the component is mounted.
     * Without this, changing steps does not reset the modal state.
     */
    const [modalKey, setModalKey] = useState(() => Math.random());

    const { form, formFields, defaultVisibleValues } = useDataStreamForm({
        formTemplate,
        defaultValues: formDefaultValues,
        streamDefinitions,
        pluginId: plugin?.id,
        pluginConfigId: pluginConfig?.id
    });

    const { configurationState, scopeIsSingleObject, dispatch } = useTileEditorStore((s) => ({
        configurationState: s.configurationState,
        scopeIsSingleObject: s.scope.type === 'objectList' && s.scope.selectedNodeIds.length === 1,
        dispatch: s.dispatch
    }));

    const menuPortalTarget = useDOMElement('dataStreamEditorSteps', null);

    // Avoid spamming requests when someone types in a text box or otherwise changes fields quickly,
    // this also delays updating the tile config, so we don't want to delay it too much
    const setConfigDebounced = useMemo(() => debounce(setConfig, 1000), [setConfig]);

    useEffect(() => {
        // Flush any pending updates when we unmount, or setConfig changes
        return () => {
            return setConfigDebounced.flush();
        };
    }, [setConfigDebounced]);

    const {
        formState: { isSubmitting, isValid },
        watch
    } = form;

    const resolveConfig = (dataSourceConfig: object) => {
        const formFieldsByName = getAllFieldsByName(formFields ?? []);

        return Object.entries(dataSourceConfig).reduce(
            (acc, [key, value]) => {
                acc[key] = getOptionValue(value);
                if (formFieldsByName.get(key)?.type === 'key-value') {
                    // Keep key-value objects verbatim
                    acc[key] = value;
                } else {
                    acc[key] = getOptionValue(value);
                }

                /**
                 * If the option has data, include it in the
                 * dataSourceConfig alongside the value.
                 */
                const data = getOptionData(value);

                if (data) {
                    acc[`${key}_data`] = data;
                }

                return acc;
            },
            {} as Record<string, unknown>
        );
    };

    const previousFormState = useRef(stringify(resolveConfig(defaultVisibleValues ?? {})));

    const setDataStreamConfig = (dataSourceConfig: object) => {
        const resolvedConfig = resolveConfig(dataSourceConfig);
        const configHasChanged = stringify(config.dataStream?.dataSourceConfig ?? {}) !== stringify(resolvedConfig);

        if (configHasChanged) {
            (manualConfigApply ? setConfig : setConfigDebounced)((c) => ({
                ...c,
                dataStream: {
                    ...c.dataStream,
                    dataSourceConfig: cloneDeep(resolvedConfig)
                }
            }));
        }
    };

    // Track form changes so we can go between docked and un-docked
    useFormChange(form.watch, (values) => trackFormChanges(values));

    const formData = watch();

    const currentFormState = stringify(resolveConfig(formData));

    const { dataStreamRequest } = useDataStreamConfigToRequest(config);

    if (!manualConfigApply && isValid && formFields != null) {
        setDataStreamConfig(formData);
    }

    const handleInteractiveTest = async (testName: string, formDataToTest: Record<string, unknown>) => {
        const pluginConfigToUse = {
            ...pluginConfig!.config!,
            agentGroupId: pluginConfig?.agentGroupId
                ? new AgentGroupId(pluginConfig.agentGroupId.toString())
                : undefined,
            ___testName: testName, // May be better to add extra arg to the whole test config route?
            ___dataSourceConfig: resolveConfig(formDataToTest)
        } as any as PluginConfigFormData;

        if (
            hasHandlebarsPlaceholders(stringify(pluginConfigToUse.___dataSourceConfig)) &&
            dataStreamRequest &&
            dataStreamRequest.dataStreamId &&
            dataStreamRequest.scope
        ) {
            const resolvedConfig = await resolveDataSourceConfig(
                dataStreamRequest.dataStreamId,
                dataStreamRequest.scope,
                pluginConfigToUse.___dataSourceConfig,
                dataStreamRequest?.timeframe,
                dataStreamRequest?.context
            );

            pluginConfigToUse.___dataSourceConfig = resolvedConfig;
        }

        const testAPI = TestConfigAPI(plugin, pluginConfig?.id);
        const result = await testAPI({ data: pluginConfigToUse });
        return result;
    };

    const isFirstFormFieldTypeText =
        (formFields != null && formFields[0].type === 'text') ||
        (formFields != null && formFields[0].type === 'textarea');

    const isTabbedInterface = formFields?.[0].type === 'fieldGroup' && formFields?.[0].displayAs === 'tabs';
    const isGlobalDatastream = isDataSourceScopedDataStream(dataStream.pluginId, dataStream.definition.matches ?? {});

    return formFields ? (
        <PluginContext.Provider
            value={{
                plugin,
                config: pluginConfig! as Record<string, unknown>,
                testFunction: handleInteractiveTest
            }}
        >
            <FormProvider {...form}>
                <form
                    className={cn('flex flex-col flex-1 h-full min-w-0 min-h-0 space-y-4', className)}
                    onSubmit={form.handleSubmit(noop)}
                >
                    <div
                        className={cn(
                            'flex-1 w-full min-h-0 flex flex-col',
                            !isTabbedInterface && 'tile-scroll-overflow pl-6 pr-5 pt-4'
                        )}
                    >
                        <DisplayJsonUi
                            includeDisplayNameField={false}
                            formFields={formFields}
                            autoFocus={isFirstFormFieldTypeText}
                            menuPortalTarget={menuPortalTarget}
                            configurationState={configurationState}
                            handleConfigurationStateChange={(fieldName: string) =>
                                (data: Record<string, string | number>) => {
                                    dispatch({
                                        type: 'updateConfigurationState',
                                        configurationState: {
                                            ...configurationState,
                                            [fieldName]: {
                                                ...((configurationState?.[fieldName] as object) ?? {}),
                                                ...data
                                            }
                                        }
                                    });
                                }}
                        />
                    </div>
                    <div className='flex justify-end flex-shrink-0 pl-6 pr-5 text-base'>
                        {allowDataStreamSaving &&
                            isGlobalDatastream &&
                            pluginConfig != null &&
                            !isSavedDataStream(dataStream) && (
                                <SaveAsDataStreamModal
                                    key={modalKey}
                                    dataSourceConfig={resolveConfig(formData)}
                                    baseDataStream={dataStream}
                                    basePluginConfigId={pluginConfig.id}
                                    config={config}
                                    onSaveDataStream={(savedDataStream) => {
                                        dispatch({
                                            type: 'dataStream.selectDataStream',
                                            dataStream: toTileEditorDataStream(savedDataStream, streamDefinitions)
                                        });

                                        setCurrentEditorStep(undefined);
                                    }}
                                    onClose={() => {
                                        setModalKey(Math.random());
                                    }}
                                >
                                    {scopeIsSingleObject ? (
                                        <Button
                                            type='button'
                                            variant='secondary'
                                            disabled={!isValid}
                                            className='mr-auto'
                                        >
                                            Save as data stream
                                        </Button>
                                    ) : (
                                        <Tooltip
                                            title='A single object must be selected to save as a data stream.'
                                            className='mr-auto'
                                            // Requried to prevent the modal from opening when the button is clicked
                                            onClick={(e: MouseEvent) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                            }}
                                        >
                                            <Button type='button' variant='secondary' disabled={true}>
                                                Save as data stream
                                            </Button>
                                        </Tooltip>
                                    )}
                                </SaveAsDataStreamModal>
                            )}

                        {manualConfigApply && (
                            <Button
                                type='button'
                                variant='secondary'
                                disabled={!isValid || previousFormState.current === currentFormState}
                                className='mr-2'
                                onClick={() => {
                                    previousFormState.current = currentFormState;
                                    setDataStreamConfig(formData);
                                }}
                            >
                                Apply
                            </Button>
                        )}

                        <StepProgressButton
                            disabled={
                                isSubmitting ||
                                !isValid ||
                                (manualConfigApply && previousFormState.current !== currentFormState)
                            }
                        />
                    </div>
                </form>
            </FormProvider>
        </PluginContext.Provider>
    ) : (
        <div className={cn('flex items-end flex-1 mt-4 text-base md:justify-end', className)}>
            <StepProgressButton />
        </div>
    );
};
